import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Image as ImageIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
  },
  currentImage: {
    width: "100%",
    maxWidth: 200,
    height: 200,
    objectFit: "contain",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
  },
  noImage: {
    width: "100%",
    maxWidth: 200,
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px dashed #ccc",
    borderRadius: 8,
    backgroundColor: "#fafafa",
    color: "#999",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  uploadArea: {
    border: "2px dashed #ccc",
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "#f0f7ff",
    },
  },
  uploadAreaDragover: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#e3f2fd",
  },
  hiddenInput: {
    display: "none",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: 200,
    objectFit: "contain",
    borderRadius: 8,
    marginTop: theme.spacing(2),
  },
  externalResultCard: {
    marginBottom: theme.spacing(1),
    border: "1px solid #e0e0e0",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  externalResultCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor: "#e3f2fd",
  },
  externalResultImage: {
    height: 100,
    objectFit: "contain",
    backgroundColor: "#f5f5f5",
  },
  tabContent: {
    padding: theme.spacing(2),
    minHeight: 300,
  },
  sourceChip: {
    fontSize: 10,
    padding: "2px 6px",
    borderRadius: 4,
    backgroundColor: "#e0e0e0",
    marginRight: theme.spacing(1),
  },
  loadingOverlay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  productInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2d3748" : "#f5f5f5",
    borderRadius: 8,
  },
}));

const ProductImageModal = ({ open, onClose, product, onUpdate }) => {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dragover, setDragover] = useState(false);

  // Upload state
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // URL state
  const [imageUrl, setImageUrl] = useState("");

  // External search state
  const [externalResults, setExternalResults] = useState([]);
  const [selectedExternal, setSelectedExternal] = useState(null);
  const [searchingExternal, setSearchingExternal] = useState(false);

  // Reset all states when modal closes
  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setPreviewUrl(null);
      setSelectedFile(null);
      setImageUrl("");
      setExternalResults([]);
      setSelectedExternal(null);
      setDragover(false);
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset states when changing tabs
    setPreviewUrl(null);
    setSelectedFile(null);
    setImageUrl("");
    setExternalResults([]);
    setSelectedExternal(null);
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem válido");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB");
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragover(true);
  };

  const handleDragLeave = () => {
    setDragover(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || !previewUrl) {
      toast.error("Selecione uma imagem primeiro");
      return;
    }

    setLoading(true);
    try {
      const result = await pharmaApi.uploadProductImage(
        product.id,
        previewUrl, // Base64 data
        selectedFile.name
      );
      toast.success("Imagem enviada com sucesso!");
      onUpdate(result);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetUrl = async () => {
    if (!imageUrl.trim()) {
      toast.error("Digite uma URL válida");
      return;
    }

    setLoading(true);
    try {
      const result = await pharmaApi.setProductImageUrl(product.id, imageUrl.trim());
      toast.success("URL da imagem atualizada com sucesso!");
      onUpdate(result);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchExternal = async () => {
    if (!product.barcode && !product.name) {
      toast.error("Produto sem código de barras ou nome para buscar");
      return;
    }

    setSearchingExternal(true);
    setExternalResults([]);
    setSelectedExternal(null);

    try {
      const response = await pharmaApi.searchExternalImages(product.id, {
        ean: product.barcode,
        productName: product.name,
      });
      const results = response.results || response || [];
      setExternalResults(results);
      if (results.length === 0) {
        toast.info("Nenhuma imagem encontrada em fontes externas");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSearchingExternal(false);
    }
  };

  const handleSelectExternal = async (result) => {
    setSelectedExternal(result);
  };

  const handleApplyExternal = async () => {
    if (!selectedExternal) {
      toast.error("Selecione uma imagem primeiro");
      return;
    }

    setLoading(true);
    try {
      const result = await pharmaApi.setProductImageUrl(product.id, selectedExternal.imageUrl);
      toast.success(`Imagem de ${selectedExternal.source} aplicada com sucesso!`);
      onUpdate(result);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!product.imageUrl) {
      toast.info("Produto não possui imagem");
      return;
    }

    setLoading(true);
    try {
      const result = await pharmaApi.deleteProductImage(product.id);
      toast.success("Imagem removida com sucesso!");
      onUpdate(result);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6">Gerenciar Imagem do Produto</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Product Info */}
        <Box className={classes.productInfo}>
          <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
            {product.name}
          </Typography>
          {product.barcode && (
            <Typography variant="body2" color="textSecondary">
              EAN: {product.barcode}
            </Typography>
          )}
        </Box>

        {/* Current Image */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" gutterBottom>
              Imagem Atual
            </Typography>
            {product.imageUrl ? (
              <Box textAlign="center">
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className={classes.currentImage}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <Box mt={1}>
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    disabled={loading}
                  >
                    Remover
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box className={classes.noImage}>
                <ImageIcon style={{ fontSize: 48, color: "#ccc" }} />
                <Typography variant="body2">Sem imagem</Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={8}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab icon={<UploadIcon />} label="Upload" />
              <Tab icon={<LinkIcon />} label="URL" />
              <Tab icon={<SearchIcon />} label="Buscar" />
            </Tabs>

            {/* Upload Tab */}
            {tabValue === 0 && (
              <Box className={classes.tabContent}>
                <Box
                  className={`${classes.uploadArea} ${dragover ? classes.uploadAreaDragover : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className={classes.hiddenInput}
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                  <UploadIcon style={{ fontSize: 40, color: "#999" }} />
                  <Typography variant="body1" style={{ marginTop: 8 }}>
                    Arraste uma imagem ou clique para selecionar
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Formatos: JPG, PNG, GIF, WebP (máx 5MB)
                  </Typography>
                </Box>

                {previewUrl && (
                  <Box textAlign="center">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className={classes.previewImage}
                    />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      {selectedFile?.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* URL Tab */}
            {tabValue === 1 && (
              <Box className={classes.tabContent}>
                <TextField
                  fullWidth
                  label="URL da Imagem"
                  placeholder="https://exemplo.com/imagem.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  variant="outlined"
                  size="small"
                  helperText="Cole a URL de uma imagem da internet"
                />

                {imageUrl && (
                  <Box textAlign="center" mt={2}>
                    <img
                      src={imageUrl}
                      alt="Preview"
                      className={classes.previewImage}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Search Tab */}
            {tabValue === 2 && (
              <Box className={classes.tabContent}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={searchingExternal ? <CircularProgress size={18} /> : <SearchIcon />}
                  onClick={handleSearchExternal}
                  disabled={searchingExternal}
                  fullWidth
                >
                  {searchingExternal ? "Buscando..." : "Buscar em Fontes Externas"}
                </Button>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, textAlign: "center" }}>
                  Busca em Open Food Facts, Cosmos e outras fontes gratuitas
                </Typography>

                {externalResults.length > 0 && (
                  <Box mt={2}>
                    <Divider style={{ marginBottom: 16 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Resultados ({externalResults.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {externalResults.map((result, index) => (
                        <Grid item xs={6} key={index}>
                          <Card
                            className={`${classes.externalResultCard} ${
                              selectedExternal === result ? classes.externalResultCardSelected : ""
                            }`}
                          >
                            <CardActionArea onClick={() => handleSelectExternal(result)}>
                              <CardMedia
                                component="img"
                                className={classes.externalResultImage}
                                image={result.thumbnailUrl || result.imageUrl}
                                alt={result.productName}
                              />
                              <Box p={1}>
                                <Typography variant="body2" noWrap>
                                  <span className={classes.sourceChip}>
                                    {result.source}
                                  </span>
                                  {result.productName}
                                </Typography>
                                {selectedExternal === result && (
                                  <CheckIcon
                                    color="primary"
                                    style={{ position: "absolute", top: 4, right: 4 }}
                                  />
                                )}
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" disabled={loading}>
          Cancelar
        </Button>

        {tabValue === 0 && (
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={loading || !previewUrl}
            startIcon={loading ? <CircularProgress size={18} /> : <UploadIcon />}
          >
            {loading ? "Enviando..." : "Enviar Imagem"}
          </Button>
        )}

        {tabValue === 1 && (
          <Button
            onClick={handleSetUrl}
            color="primary"
            variant="contained"
            disabled={loading || !imageUrl.trim()}
            startIcon={loading ? <CircularProgress size={18} /> : <LinkIcon />}
          >
            {loading ? "Salvando..." : "Usar URL"}
          </Button>
        )}

        {tabValue === 2 && (
          <Button
            onClick={handleApplyExternal}
            color="primary"
            variant="contained"
            disabled={loading || !selectedExternal}
            startIcon={loading ? <CircularProgress size={18} /> : <CheckIcon />}
          >
            {loading ? "Aplicando..." : "Usar Selecionada"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductImageModal;
